import { Skeleton, Stack, Typography } from "@mui/material";
import { PickerValue } from "@mui/x-date-pickers/internals";
import { differenceInYears, format, parseISO } from "date-fns";
import {
  useCallback,
  useEffect,
  useState,
  useMemo,
  lazy,
  Suspense,
} from "react";
import { useTranslation } from "react-i18next";
import { useUpdateMeMutation } from "src/api/services/patientApi";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { usePatient } from "src/context/PatientProvider";
import { useSubmitAnswers } from "src/hooks/useSubmitAnswers";

const WheelDatePicker = lazy(() => import("src/components/WheelDatePicker"));

const DateOfBirth = () => {
  const [updatePatient, { isLoading }] = useUpdateMeMutation();
  const patientDob = usePatient((patient) => patient.profile.dob);
  const [dobString, setDobString] = useState<string>("");
  const [dobDate, setDobDate] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const { submitAnswer } = useSubmitAnswers();

  const label = useMemo(() => t("dob.label", "Birthday"), [t]);

  const handleOnSubmit = useCallback(async () => {
    try {
      let msg: string = "";

      if (!dobDate) {
        msg = t(
          `dob.missing-data`,
          "Please enter your date of birth to continue",
        );

        setError(msg);
        return;
      }

      if (dobDate < new Date("1901/01/01")) {
        msg = t(
          `dob.incorrect-data`,
          "Date should not be before 1st of January 1901",
        );

        setError(msg);
        return;
      }

      await updatePatient({ profile: { dob: dobString } }).unwrap();

      const age = differenceInYears(new Date(), dobDate);
      if (age < 18) {
        msg = t(
          `dob.unmet-age-requirement`,
          "Must be 18 or older to continue.",
        );
        setError(msg);
        return;
      }

      submitAnswer([dobString]);
    } catch (err) {
      console.error("Error updating patient", err);
      setError("Error saving date of birth");
    }
  }, [dobDate, updatePatient, dobString, submitAnswer, t]);

  const handleDate = useCallback((newDate: PickerValue) => {
    setError("");
    setDobDate(newDate);
    if (newDate) {
      setDobString(format(newDate, "yyyy-MM-dd"));
    } else {
      setDobString("");
    }
  }, []);

  useEffect(() => {
    if (patientDob !== null) {
      setDobString(patientDob);
      setDobDate(parseISO(patientDob));
    }
  }, [patientDob]);

  return (
    <Stack gap={3}>
      <Stack gap={4}>
        <Suspense fallback={<Skeleton variant="rounded" height={56} />}>
          <WheelDatePicker
            label={label}
            onChange={handleDate}
            value={dobDate}
          />
        </Suspense>
      </Stack>
      {error ? (
        <Typography sx={{ color: "red" }} textAlign="center">
          {error}
        </Typography>
      ) : null}

      <LoadingButton onClick={handleOnSubmit} loading={isLoading}>
        {t("button.next", "Next")}
      </LoadingButton>
    </Stack>
  );
};

export default DateOfBirth;
